import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getTerms, getTermVersionHistory, getTermVersionDetail } from '../api/terms';
import styles from './Terms.module.css';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import Breadcrumb from '../components/Breadcrumb';

// 좁은 화면에서 표가 페이지 폭을 밀지 않도록 감싼다.
const MD_COMPONENTS = {
  table: (props) => <div className={styles.tableScroll}><table {...props} /></div>,
};

function stripSihaengAn(title) {
  return (title ?? '').replace(/\s*시행안$/, '').trim();
}

export default function PrivacyPolicy() {
  useDocumentMeta({ title: '개인정보처리방침' });
  const [termId, setTermId] = useState(null);
  const [versions, setVersions] = useState([]);
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  const [content, setContent] = useState('');
  const [noticeAt, setNoticeAt] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const terms = await getTerms();
        const term = terms.find(t => t.type === 'PRIVACY');
        if (!term) { setError(true); return; }

        setTermId(term.termId);
        setContent(term.content ?? '');

        const history = await getTermVersionHistory(term.termId);
        setVersions(history.versions ?? []);

        const current = (history.versions ?? []).find(v => v.current);
        if (current) {
          setSelectedVersionId(current.versionId);
          setNoticeAt(current.noticeAt ?? '');
        }
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const handleVersionChange = async (versionId) => {
    setSelectedVersionId(versionId);
    try {
      const detail = await getTermVersionDetail(termId, versionId);
      setContent(detail.content ?? '');
      setNoticeAt(detail.noticeAt ?? '');
    } catch {
      // keep existing content on error
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.inner}>
        <Breadcrumb current="개인정보처리방침" />

        <header className={styles.head}>
          <div className={styles.titleRow}>
            <div>
              <h1 className={styles.pageTitle}>개인정보처리방침</h1>
              {noticeAt && <p className={styles.updated}>시행일 {noticeAt}</p>}
            </div>
            {versions.length > 0 && (
              <select
                className={styles.dateSelect}
                value={selectedVersionId ?? ''}
                onChange={e => handleVersionChange(e.target.value)}
                aria-label="버전 선택"
              >
                {versions.map(v => (
                  <option key={v.versionId} value={v.versionId}>{stripSihaengAn(v.title)}</option>
                ))}
              </select>
            )}
          </div>
        </header>

        {loading && <p className={styles.stateMsg}>불러오는 중...</p>}
        {!loading && error && <p className={styles.stateMsg}>약관을 불러올 수 없습니다.</p>}
        {!loading && !error && (
          <div className={styles.mdContent}>
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>{content}</ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
}
